import React, { useCallback, useEffect, useState } from 'react';

interface ImageLightboxProps {
  images: string[];
  initialIndex: number;
  onClose: () => void;
}

/**
 * Image lightbox component for viewing images in a modal
 */
export const ImageLightbox: React.FC<ImageLightboxProps> = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isVisible, setIsVisible] = useState(false);

  // Animate in on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsVisible(false);
    // Wait for animation to complete before calling onClose
    setTimeout(onClose, 200);
  }, [onClose]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      } else if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images.length, handleClose]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div
      className={`wc-fixed wc-inset-0 wc-z-[9999] wc-flex wc-items-center wc-justify-center wc-transition-opacity wc-duration-200 ${
        isVisible ? 'wc-opacity-100' : 'wc-opacity-0'
      }`}
      onClick={handleOverlayClick}
    >
      {/* Dark overlay */}
      <div className="wc-absolute wc-inset-0 wc-bg-black/90" />

      {/* Close button */}
      <button
        onClick={handleClose}
        className="wc-absolute wc-top-4 wc-right-4 wc-z-10 wc-w-10 wc-h-10 wc-flex wc-items-center wc-justify-center wc-bg-white/10 hover:wc-bg-white/20 wc-text-white wc-rounded-full wc-transition-colors wc-backdrop-blur-sm wc-border-none"
        aria-label="Close"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="wc-w-6 wc-h-6"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Image counter */}
      {images.length > 1 && (
        <div className="wc-absolute wc-top-4 wc-left-1/2 wc--translate-x-1/2 wc-z-10 wc-px-4 wc-py-2 wc-bg-black/50 wc-text-white wc-rounded-full wc-text-sm wc-backdrop-blur-sm">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Previous button */}
      {images.length > 1 && currentIndex > 0 && (
        <button
          onClick={handlePrevious}
          className="wc-absolute wc-left-4 wc-z-10 wc-w-12 wc-h-12 wc-flex wc-items-center wc-justify-center wc-bg-white/10 hover:wc-bg-white/20 wc-text-white wc-rounded-full wc-transition-colors wc-backdrop-blur-sm"
          aria-label="Previous image"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="wc-w-6 wc-h-6"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
      )}

      {/* Next button */}
      {images.length > 1 && currentIndex < images.length - 1 && (
        <button
          onClick={handleNext}
          className="wc-absolute wc-right-4 wc-z-10 wc-w-12 wc-h-12 wc-flex wc-items-center wc-justify-center wc-bg-white/10 hover:wc-bg-white/20 wc-text-white wc-rounded-full wc-transition-colors wc-backdrop-blur-sm"
          aria-label="Next image"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="wc-w-6 wc-h-6"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      )}

      {/* Image */}
      <div className="wc-relative wc-z-[1] wc-max-w-[90vw] wc-max-h-[90vh] wc-flex wc-items-center wc-justify-center">
        <img
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className={`wc-max-w-full wc-max-h-[90vh] wc-object-contain wc-rounded-lg wc-shadow-2xl wc-transition-all wc-duration-300 ${
            isVisible ? 'wc-scale-100' : 'wc-scale-95'
          }`}
        />
      </div>
    </div>
  );
};
